import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Tabs,
  Tab,
  Typography,
  Stack,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  TextField,
} from '@mui/material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { format, parseISO } from 'date-fns';
import api, { matchApi } from '../services/api';
import { delphiService, type PromptTemplate } from '../services/delphiApi';
import { useNavigate } from 'react-router-dom';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import Visibility from '@mui/icons-material/Visibility';
import IconButton from '@mui/material/IconButton';
import GeminiModelSelect from '../components/GeminiModelSelect';

type Sport = 'cricket' | 'football' | 'basketball';

interface MatchRow {
  id: number;
  name: string;
  series_name?: string;
  series_id?: number;
  slug?: string;
  series_slug?: string;
  start_date?: string;
  visibility?: string;
  tournament_name?: string;
  is_visible?: boolean;
  articles?: { id: number; title: string; slug: string; is_draft: boolean; published: boolean }[];
  ai_session_id?: number | null;
}

const useMatchFetcher = (sport: Sport, active: boolean) => {
  const [rows, setRows] = useState<MatchRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  const fetcher = async (pageArg = page, pageSizeArg = pageSize) => {
    setLoading(true);
    try {
      const params: any = {
        page: pageArg + 1,
        page_size: pageSizeArg,
        ordering: 'start_date',
      };
      // upcoming filter
      params.start_time_after = new Date().toISOString();

      let resp;
      if (sport === 'cricket') {
        resp = await matchApi.getAll({ ...params, include_articles: 'true', include_ai_session: 'true' });
      } else {
        resp = await api.get(`/${sport}/matches/`, { params });
      }
      const data = resp.data;
      setTotal(data.count || 0);
      const mapped = (data.results || data || []).map((m: any) => ({
        id: m.id,
        name: m.name || m.title || 'Match',
        series_name: m.series?.name || m.league?.name || m.tournament?.name,
        slug: m.slug,
        series_slug: m.series?.slug || m.league?.slug || m.tournament?.slug,
        series_id: m.series?.id || m.league?.id || m.tournament?.id,
        tournament_name: m.league?.name || m.tournament?.name,
        start_date: m.start_date || m.kickoff_time || m.start_time,
        visibility: m.match_status || m.status,
        is_visible: m.is_visible,
        articles: m.articles || [],
        ai_session_id: m.ai_session_id ?? null,
      }));
      setRows(mapped);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (active) {
      fetcher();
    }
  }, [page, pageSize, active]);

  return { rows, loading, total, page, pageSize, setPage, setPageSize, refetch: fetcher };
};

const ContentDashboard: React.FC = () => {
  const [tab, setTab] = useState<Sport>('cricket');
  const cricket = useMatchFetcher('cricket', tab === 'cricket');
  const football = useMatchFetcher('football', tab === 'football');
  const basketball = useMatchFetcher('basketball', tab === 'basketball');
  const navigate = useNavigate();
  
  // Prompt selector state
  const [promptDialogOpen, setPromptDialogOpen] = useState(false);
  const [selectedMatchId, setSelectedMatchId] = useState<number | null>(null);
  const [, setSelectedSeriesId] = useState<number | null>(null);
  const [availablePrompts, setAvailablePrompts] = useState<PromptTemplate[]>([]);
  const [selectedPromptKey, setSelectedPromptKey] = useState<string>('');
  const [seriesPromptName, setSeriesPromptName] = useState<string | null>(null);
  const [loadingPrompts, setLoadingPrompts] = useState(false);
  const [creatingSession, setCreatingSession] = useState(false);
  const [additionalInstruction, setAdditionalInstruction] = useState<string>('');
  const [selectedLlmModel, setSelectedLlmModel] = useState<string>('');

  const current = useMemo(() => {
    if (tab === 'cricket') return cricket;
    if (tab === 'football') return football;
    return basketball;
  }, [tab, cricket, football, basketball]);

  // Load prompts when the dialog opens and pre-select the one the server will use for this match: the series' own (or its
  // tour's) prompt when it has one, else the system default. The server decides (GET prompt-defaults), so this screen,
  // regenerate in the editor and the automatic drafts all agree.
  useEffect(() => {
    if (!promptDialogOpen) return;

    let cancelled = false;
    setLoadingPrompts(true);
    setSeriesPromptName(null);

    Promise.all([
      delphiService.listPrompts({ prompt_type: 'initial_generation' }),
      selectedMatchId
        ? delphiService.getPromptDefaults({ app_label: 'cricket', model: 'Match', object_id: selectedMatchId }).catch(() => null)
        : Promise.resolve(null),
    ])
      .then(([prompts, defaults]) => {
        if (cancelled) return;
        setAvailablePrompts(prompts);

        const chosen = defaults?.initial?.key ? prompts.find((p) => p.key === defaults.initial.key) : undefined;
        if (chosen) {
          setSelectedPromptKey(chosen.key);
          if (defaults?.initial?.source === 'series') setSeriesPromptName("this match's series");
          return;
        }

        const defaultPrompt = prompts.find((p) => p.is_default);
        if (defaultPrompt) {
          setSelectedPromptKey(defaultPrompt.key);
        } else if (prompts.length > 0) {
          setSelectedPromptKey(prompts[0].key);
        }
      })
      .catch((err) => console.error('Failed to load prompts:', err))
      .finally(() => {
        if (!cancelled) setLoadingPrompts(false);
      });

    return () => {
      cancelled = true;
    };
  }, [promptDialogOpen, selectedMatchId]);

  const handleOpenPromptDialog = (matchId: number, seriesId?: number | null) => {
    setSelectedMatchId(matchId);
    setSelectedSeriesId(seriesId ?? null);
    setPromptDialogOpen(true);
  };

  const handleClosePromptDialog = () => {
    setPromptDialogOpen(false);
    setSelectedMatchId(null);
    setSelectedSeriesId(null);
    setSeriesPromptName(null);
    setAdditionalInstruction('');
  };

  const handleCreateSession = async () => {
    if (!selectedMatchId) return;

    setCreatingSession(true);
    try {
      const session = await delphiService.initSession({
        app_label: 'cricket',
        model: 'Match',
        object_id: selectedMatchId,
        prompt_key: selectedPromptKey || undefined,
        additional_instruction: additionalInstruction.trim() || undefined,
        llm_model: selectedLlmModel || undefined,
      });
      handleClosePromptDialog();
      navigate(`/delphi/${session.id}`);
    } catch (e) {
      console.error('Failed to create session:', e);
    } finally {
      setCreatingSession(false);
    }
  };

  const handleOpenMatch = (sport: Sport, matchId: number) => {
    if (sport === 'cricket') navigate(`/matches/${matchId}/view`);
    else navigate(`/${sport}/matches/${matchId}`);
  };

  const handleOpenSeries = (sport: Sport, row: MatchRow) => {
    if (sport === 'cricket') navigate(`/series/${row.series_id}/view`);
    else navigate(`/${sport}/tournaments/${row.id}`);
  };

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 80 },
    {
      field: 'name',
      headerName: 'Match',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => {
        const row = params.row as MatchRow;
        const url = row.slug && row.series_slug ? `https://ai11.in/series/${row.series_slug}/match/${row.slug}` : undefined;
        return (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
            {url && (
              <IconButton size="small" href={url} target="_blank" rel="noopener noreferrer">
                <OpenInNewIcon fontSize="inherit" />
              </IconButton>
            )}
            <Typography variant="body2" noWrap>
              {row.name}
            </Typography>
          </Stack>
        );
      },
    },
    {
      field: 'series_name',
      headerName: 'Series/Tournament',
      flex: 1,
      minWidth: 300,
      renderCell: (params) => {
        const row = params.row as MatchRow;
        const url = row.series_slug ? `https://ai11.in/series/${row.series_slug}` : undefined;
        return (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
            {url && (
              <IconButton size="small" href={url} target="_blank" rel="noopener noreferrer">
                <OpenInNewIcon fontSize="inherit" />
              </IconButton>
            )}
            <Typography variant="body2" noWrap>
              {row.series_name || '—'}
            </Typography>
          </Stack>
        );
      },
    },
    {
      field: 'articles',
      headerName: 'Articles',
      flex: 1,
      minWidth: 360,
      renderCell: (params) => {
        if (tab !== 'cricket') {
          return <Typography variant="body2" color="text.secondary">—</Typography>;
        }
        const list = (params.row as MatchRow).articles || [];
        if (!list.length) {
          return (  
            <Button size="small"  variant="outlined" onClick={() => navigate('/cms/articles/new')}>
              Create New
            </Button>
          );
        }
        return (
          <Stack spacing={0.75} sx={{ minWidth: 0 }}>
            {list.map((article) => {
              const externalUrl = article.slug ? `https://ai11.in/blogs/${article.slug}` : undefined;
              return (
                <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }} key={article.id}>
                  <Typography variant="body2" noWrap>{article.title}</Typography>
                  {externalUrl && (
                    <IconButton size="small" href={externalUrl} target="_blank" rel="noopener noreferrer">
                      <OpenInNewIcon fontSize="inherit" />
                    </IconButton>
                  )}
                  <IconButton size="small" onClick={() => navigate(`/cms/articles/${article.id}/view`)}>
                    <Visibility />
                  </IconButton>
                </Stack>
              );
            })}
          </Stack>
        );
      },
    },
    {
      field: 'ai_session',
      headerName: 'AI Session',
      width: 100,
      renderCell: (params) => {
        if (tab !== 'cricket') {
          return <Typography variant="body2" color="text.secondary">—</Typography>;
        }
        const sessionId = (params.row as MatchRow).ai_session_id;
        return sessionId ? (
          <Button size="small" variant="contained" onClick={() => navigate(`/delphi/${sessionId}`)}>
            Edit
          </Button>
        ) : (
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              const row = params.row as MatchRow;
              handleOpenPromptDialog(row.id, row.series_id);
            }}
          >
            New
          </Button>
        );
      },
    },
    {
      field: 'start_date',
      headerName: 'Start Time',
      width: 150,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.value ? format(parseISO(params.value as string), 'dd MMM yyyy, HH:mm') : 'TBD'}
        </Typography>
      ),
    },
    {
      field: 'visibility',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => <Chip size="small" label={params.value || '—'} />,
    },
    {
      field: 'is_visible',
      headerName: 'Visible',
      width: 70,
      renderCell: (params) => {
        const val = (params.row as MatchRow).is_visible;
        if (val === true) return <Chip size="small" color="success" label="Yes" />;
        if (val === false) return <Chip size="small" color="default" label="No" />;
        return <Typography variant="body2" color="text.secondary">—</Typography>;
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 160,
      sortable: false,
      renderCell: (params) => {
        const row = params.row as MatchRow;
        return (
          <Stack direction="row" spacing={1}>
            <Button size="small" variant="outlined" onClick={() => handleOpenMatch(tab, row.id)}>
              Match
            </Button>
            <Button size="small" variant="outlined" onClick={() => handleOpenSeries(tab, row)}>
              Series
            </Button>
          </Stack>
        );
      },
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        Content Dashboard
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Upcoming matches for quick access to previews, articles, and AI sessions.
      </Typography>

      <Paper sx={{ mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab label="Cricket" value="cricket" />
          <Tab label="Football" value="football" />
          <Tab label="Basketball" value="basketball" />
        </Tabs>
      </Paper>

      <Paper sx={{ p: 1 }}>
        <DataGrid
          rows={current.rows}
          columns={columns}
          loading={current.loading}
          rowCount={current.total}
          paginationModel={{ page: current.page, pageSize: current.pageSize }}
          paginationMode="server"
          onPaginationModelChange={(m) => {
            current.setPage(m.page);
            current.setPageSize(m.pageSize || 25);
          }}
          getRowId={(row) => row.id}
          density="compact"
          disableRowSelectionOnClick
        />
      </Paper>

      {/* Prompt Selector Dialog */}
      <Dialog open={promptDialogOpen} onClose={handleClosePromptDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Select Initial Prompt</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Choose a prompt template and (optionally) add extra instructions for generating the initial content.
          </Typography>
          {loadingPrompts ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Stack spacing={2}>
              {seriesPromptName && (
                <Typography variant="caption" color="primary">
                  Pre-selected the custom prompt template of {seriesPromptName} - change it below if needed.
                </Typography>
              )}
              <FormControl fullWidth>
                <InputLabel>Prompt Template</InputLabel>
                <Select
                  value={selectedPromptKey}
                  label="Prompt Template"
                  onChange={(e) => setSelectedPromptKey(e.target.value)}
                >
                  {availablePrompts.map((prompt) => (
                    <MenuItem key={prompt.key} value={prompt.key}>
                      <Box>
                        <Typography variant="body2">
                          {prompt.name} {prompt.is_default && '(Default)'}
                        </Typography>
                        {prompt.description && (
                          <Typography variant="caption" color="text.secondary">
                            {prompt.description}
                          </Typography>
                        )}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <GeminiModelSelect value={selectedLlmModel} onChange={setSelectedLlmModel} />
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Additional Instructions (Optional)
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                  Add any match-specific notes or emphasis you want the AI to consider when generating content.
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  maxRows={6}
                  value={additionalInstruction}
                  onChange={(e) => setAdditionalInstruction(e.target.value)}
                  placeholder="e.g., Focus on key player battles, emphasize recent form, mention weather conditions..."
                />
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePromptDialog} disabled={creatingSession}>
            Cancel
          </Button>
          <Button
            onClick={handleCreateSession}
            variant="contained"
            disabled={!selectedPromptKey || creatingSession}
          >
            {creatingSession ? <CircularProgress size={20} /> : 'Create Session'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ContentDashboard;

